import { useCallback, useEffect, useState } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';
import {
  Currency,
  ExchangeTransaction,
  formatBalance,
  formatDate,
  PermissionEnum,
} from '@ui/src';
import {
  useAddCommentToProblemTransactionMutation,
  useCreateProblemTransactionChatMutation,
  useDeleteCommentInProblemTransactionMutation,
  useEditCommentInProblemTransactionMutation,
  useLazyGetCustomerExchangeTransactionQuery,
  useLazyGetCustomersAccountBalanceQuery,
  useLazyGetCustomersExchangeStatementQuery,
  useLazyGetCustomersTransactionLogsQuery,
  useLazyGetProblemTransactionChatsQuery,
  useLazyGetProblemTransactionCommentsQuery,
  useLazyGetProblemTransactionSenderClientsOfficiasQuery,
  useLazyGetTransactionStatementsChangeLogsQuery,
} from '../../../../customers/store/services/customers';
import { Employee } from '../../../../../store/dictionaries/types';
import { Balance } from '../../../types';

const useProblemExchangeTransaction = ({
  transactionId,
  activePermissions,
  backPath,
  customerId,
  transaction,
  currenciesList,
  currentEmployee,
}: {
  transactionId?: string;
  activePermissions?: PermissionEnum[];
  backPath?: string;
  transaction?: ExchangeTransaction & { exchangeCurrencyFixedSum: string };
  customerId?: string;
  currenciesList?: Currency[];
  currentEmployee?: Employee;
}) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [fromAccountBalance, setFromAccountBalance] = useState<number>();
  const [fromAccountBalances, setFromAccountBalances] = useState<Balance[]>([]);
  const [toAccountBalance, setToAccountBalance] = useState<number>();
  const [toAccountBalances, setToAccountBalances] = useState<Balance[]>([]);

  const navigate = useNavigate();
  const { search } = useLocation();

  const [getExcahngeLogs, { isLoading: logsIsLoading }] =
    useLazyGetCustomersTransactionLogsQuery();
  const [getCustomerExchangeTransaction, { isLoading, isFetching }] =
    useLazyGetCustomerExchangeTransactionQuery();
  const [getChats] = useLazyGetProblemTransactionChatsQuery();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetTransactionStatementsChangeLogsQuery();
  const [createTransactionChat, { isLoading: chatCreateInProgress }] =
    useCreateProblemTransactionChatMutation();
  const [getSenderAccounts] =
    useLazyGetProblemTransactionSenderClientsOfficiasQuery();
  const [
    getTransactionComments,
    { isLoading: commentsIsLoading, isFetching: commentsIsFetching },
  ] = useLazyGetProblemTransactionCommentsQuery();
  const [
    addCommentToTransaction,
    { isLoading: addCommentInProgress, isSuccess: addCommentIsSuccess },
  ] = useAddCommentToProblemTransactionMutation();
  const [
    editCommentInTransaction,
    {
      isLoading: editTransactionCommentInProgress,
      isSuccess: editTrancastionCommentIsSuccess,
    },
  ] = useEditCommentInProblemTransactionMutation();
  const [
    deleteCommentInTransaction,
    {
      isLoading: deleteCommentInTransactionInProgress,
      isSuccess: deleteCommentInTranscationIsSuccess,
    },
  ] = useDeleteCommentInProblemTransactionMutation();
  const [getFromAccountBalance] = useLazyGetCustomersAccountBalanceQuery();
  const [getToAccountBalance] = useLazyGetCustomersAccountBalanceQuery();
  const [getExchangeStatement, { isLoading: statementsIsLoading }] =
    useLazyGetCustomersExchangeStatementQuery();

  const getExchangeLogsAction = useCallback(() => {
    getExcahngeLogs(transactionId as string);
  }, [transactionId]);

  const getExchangeStatementAction = useCallback(() => {
    getExchangeStatement(transactionId as string);
  }, [transactionId]);

  const getFromAccountBalanceAction = useCallback(async () => {
    const response = (await getFromAccountBalance({
      id: transaction?.senderAccount.id,
      currencyId: transaction?.exchangeCurrencyFromId as string,
    })) as { data: { data: { edges: { node: Balance }[] } } };

    if (response.data) {
      setFromAccountBalance(response.data.data.edges[0].node.value);
      setFromAccountBalances(
        response.data.data.edges?.map((item) => item.node),
      );
    }
  }, [transaction]);

  const getToAccountBalanceAction = useCallback(async () => {
    const response = (await getToAccountBalance({
      id: transaction?.recipientAccount.id,
      currencyId: transaction?.exchangeCurrencyToId as string,
    })) as { data: { data: { edges: { node: Balance }[] } } };

    if (response.data) {
      setToAccountBalance(response.data.data.edges[0].node.value);
      setToAccountBalances(response.data.data.edges?.map((item) => item.node));
    }
  }, [transaction]);

  const onAddCommentToTransaction = async (text: string) => {
    await addCommentToTransaction({
      id: transactionId as string,
      url: `transactions-exchange`,
      body: {
        text,
      },
    });
  };

  const onEditCommentInTransaction = async ({
    text,
    commentId,
  }: {
    text: string;
    commentId: string;
  }) => {
    await editCommentInTransaction({
      id: transactionId as string,
      url: `transactions-exchange`,
      commentId,
      body: {
        text,
      },
    });
  };

  const onDeleteCommentInTransaction = async (commentId: string) => {
    await deleteCommentInTransaction({
      id: transactionId as string,
      url: `transactions-exchange`,
      commentId,
    });
  };

  const getTransactionCommentsAction = async (cursor?: string) => {
    await getTransactionComments({
      id: transactionId as string,
      url: `transactions-exchange`,
      cursor: cursor || '',
    });
  };

  useEffect(() => {
    if (
      activePermissions?.includes(
        PermissionEnum.TRANSACTIONS_EXCHANGE_COMMENTS_LIST,
      )
    ) {
      getTransactionCommentsAction();
    }
  }, []);

  const onCreateTransactionChat = (userIds?: string[]) => {
    const title = `RFI: Exchange transaction From ${formatBalance(
      Number(transaction?.exchangeCurrencyFixedSum) || 0,
      currenciesList?.find(
        (item) => item.id === transaction?.exchangeCurrencyFromId,
      ),
    )} ${transaction?.exchangeCurrencyFrom?.name} To ${formatBalance(
      Number(transaction?.exchangeCurrencyFlexibleSum) || 0,
      currenciesList?.find(
        (item) => item.id === transaction?.exchangeCurrencyToId,
      ),
    )} ${transaction?.exchangeCurrencyTo?.name}  ${
      transaction?.transferDate ? formatDate(transaction?.transferDate) : ''
    }`;

    if (!userIds?.length) {
      createTransactionChat({
        id: transactionId as string,
        body: {
          title,
          employeeId: currentEmployee?.id as string,
          organizationId: transaction?.senderAccount?.organizationId,
        },
      });
    } else {
      Promise.all(
        userIds.map((item) =>
          createTransactionChat({
            id: transactionId as string,
            body: {
              title,
              employeeId: currentEmployee?.id as string,
              organizationId: transaction?.senderAccount?.organizationId,
              userId: item,
            },
          }),
        ),
      );
    }
  };

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=TRANSACTION&entityId=${transactionId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [transactionId],
  );

  const getExchangeTransactionById = useCallback(() => {
    getCustomerExchangeTransaction(transactionId as string);

    if (
      activePermissions?.includes(
        PermissionEnum.TRANSACTION_QUEUE_ITEMS_EXCHANGE_LIST,
      )
    ) {
      getExcahngeLogs(transactionId as string);
    }

    if (activePermissions?.includes(PermissionEnum.CHANNELS_LIST)) {
      getChats(transactionId as string);
    }
  }, [transactionId, activePermissions]);

  const handleClose = () => {
    if (backPath) {
      setPanelIsOpen(false);

      setTimeout(() => {
        navigate(
          generatePath(`${backPath}${search || ''}`, {
            customerId,
          }),
        );
      }, 100);
    }
  };

  useEffect(() => {
    if (
      transaction &&
      transaction.senderAccount &&
      transaction.senderAccount?.organization?.type === 'CORPORATE'
    ) {
      getSenderAccounts(transaction.senderAccount.organizationId);
    }
  }, [transaction]);

  return {
    panelIsOpen,
    handleClose,
    search,
    logsIsLoading,
    isLoading,
    isFetching,
    changeLogsIsLoading,
    changeLogsIsFetching,
    getChangeLogsAction,
    onCreateTransactionChat,
    chatCreateInProgress,
    commentsIsLoading,
    commentsIsFetching,
    getTransactionCommentsAction,
    onAddCommentToTransaction,
    addCommentInProgress,
    addCommentIsSuccess,
    onEditCommentInTransaction,
    editTransactionCommentInProgress,
    editTrancastionCommentIsSuccess,
    onDeleteCommentInTransaction,
    deleteCommentInTransactionInProgress,
    deleteCommentInTranscationIsSuccess,
    getExchangeStatementAction,
    getFromAccountBalanceAction,
    getToAccountBalanceAction,
    fromAccountBalance,
    toAccountBalance,
    statementsIsLoading,
    fromAccountBalances,
    toAccountBalances,
    getExchangeLogsAction,
    setPanelIsOpen,
    getExchangeTransactionById,
  };
};

export default useProblemExchangeTransaction;
